import { api } from './client';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface AdminUserResumo {
  id: string;
  nome: string;
  email: string;
}

export type UserRole = 'advogado' | 'suporte' | 'admin';

export interface AdminUserListItem {
  id: string;
  auth0_id: string | null;
  nome: string;
  email: string;
  role: UserRole | null;
  is_admin: boolean;
  is_active: boolean;
  email_verified: boolean;
  onboarding_completado: boolean;
  created_at: string;
  has_oab: boolean;
}

export interface AdminUserListResponse {
  items: AdminUserListItem[];
  total: number;
  page: number;
  page_size: number;
  // Agregados globais (todo o resultado da busca, não apenas a página atual).
  total_admins: number;
  total_blocked: number;
  total_pending: number;
}

export interface AdminUserOab {
  numero: string;
  uf: string;
  tipo: string;
  verificada: boolean;
}

export interface AdminUserDetail extends AdminUserListItem {
  cpf: string | null;
  identidade_verificada: boolean;
  has_active_certificate: boolean;
  oabs: AdminUserOab[];
}

export interface AdminUserListParams {
  page?: number;
  page_size?: number;
  search?: string;
  role?: UserRole;
  active?: boolean;
}

export interface AdminUserInvitePayload {
  email: string;
  nome: string;
  is_admin: boolean;
}

export interface AdminUserUpdatePayload {
  nome?: string;
  is_admin?: boolean;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function searchAdminUsers(q: string, limit = 20): Promise<AdminUserResumo[]> {
  if (!q || q.length < 2) return [];
  const { data } = await api.get<AdminUserResumo[]>('/admin/users/search', {
    params: { q, limit },
  });
  return data;
}

export async function listAdminUsers(
  params: AdminUserListParams = {},
): Promise<AdminUserListResponse> {
  const { data } = await api.get<AdminUserListResponse>('/admin/users', { params });
  return data;
}

export async function getAdminUser(id: string): Promise<AdminUserDetail> {
  const { data } = await api.get<AdminUserDetail>(`/admin/users/${id}`);
  return data;
}

export async function inviteAdminUser(
  payload: AdminUserInvitePayload,
): Promise<AdminUserDetail> {
  const { data } = await api.post<AdminUserDetail>('/admin/users', payload);
  return data;
}

export async function updateAdminUser(
  id: string,
  payload: AdminUserUpdatePayload,
): Promise<AdminUserDetail> {
  const { data } = await api.patch<AdminUserDetail>(`/admin/users/${id}`, payload);
  return data;
}

export async function setAdminUserBlocked(
  id: string,
  blocked: boolean,
): Promise<AdminUserDetail> {
  const { data } = await api.post<AdminUserDetail>(`/admin/users/${id}/bloquear`, {
    blocked,
  });
  return data;
}

export async function resendAdminInvite(id: string): Promise<void> {
  await api.post(`/admin/users/${id}/reenviar-convite`);
}

export async function forceAdminUserEmailVerified(id: string): Promise<AdminUserDetail> {
  const { data } = await api.post<AdminUserDetail>(
    `/admin/users/${id}/forcar-verificacao-email`,
  );
  return data;
}

export async function deleteAdminUser(id: string): Promise<void> {
  await api.delete(`/admin/users/${id}`);
}
